import * as React from 'react';
import { ImageOff } from 'lucide-react';
import { urlsImagemProduto } from '@wms/shared';
import { cn } from '@/lib/utils';

/**
 * Galeria do produto.
 *
 * A API devolve as URLs CANDIDATAS (o padrão do servidor de arquivos), não as
 * que existem — não há índice do que foi publicado. Então pedimos as cinco e
 * cada uma que falhar simplesmente some. Se nenhuma vingar, dizemos que o
 * produto não tem foto, em vez de deixar um ícone quebrado na tela.
 */
export function GaleriaProduto({
  sku,
  candidatas,
  alt,
}: {
  sku: number;
  candidatas?: readonly string[] | undefined;
  alt: string;
}): JSX.Element {
  const urls = React.useMemo(
    () => (candidatas && candidatas.length > 0 ? [...candidatas] : urlsImagemProduto(sku, 5)),
    [candidatas, sku],
  );

  const [falhas, definirFalhas] = React.useState<ReadonlySet<number>>(new Set());
  const [selecionado, definirSelecionado] = React.useState(0);

  React.useEffect(() => {
    definirFalhas(new Set());
    definirSelecionado(0);
  }, [sku]);

  const registrarFalha = (indice: number): void => {
    definirFalhas((atual) => {
      const proximo = new Set(atual);
      proximo.add(indice);
      if (indice === selecionado) {
        const substituto = urls.findIndex((_, i) => !proximo.has(i));
        definirSelecionado(substituto < 0 ? 0 : substituto);
      }
      return proximo;
    });
  };

  const visiveis = urls.map((_, i) => i).filter((i) => !falhas.has(i));
  const principal = urls[selecionado];

  if (visiveis.length === 0) {
    return (
      <div className="flex h-44 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-borda bg-superficie2 text-textoFraco">
        <ImageOff className="h-7 w-7" />
        <p className="text-xs">Este produto não tem foto publicada.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex h-44 items-center justify-center overflow-hidden rounded-lg border border-borda bg-white">
        {principal && !falhas.has(selecionado) ? (
          <img
            src={principal}
            alt={alt}
            loading="lazy"
            decoding="async"
            className="max-h-full max-w-full object-contain"
            onError={() => registrarFalha(selecionado)}
          />
        ) : null}
      </div>

      {/* As miniaturas também servem de sonda: a que falhar se remove sozinha. */}
      <div className={cn('mt-2 flex flex-wrap gap-1.5', visiveis.length < 2 && 'hidden')}>
        {urls.map((url, indice) =>
          falhas.has(indice) ? null : (
            <button
              key={url}
              type="button"
              onClick={() => definirSelecionado(indice)}
              aria-label={`Ver imagem ${indice + 1}`}
              aria-pressed={selecionado === indice}
              className={cn(
                'h-12 w-12 overflow-hidden rounded border bg-white p-0.5',
                selecionado === indice ? 'border-primaria ring-1 ring-primaria' : 'border-borda',
              )}
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-contain"
                onError={() => registrarFalha(indice)}
              />
            </button>
          ),
        )}
      </div>
    </div>
  );
}
